import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  getInboundUtmContext,
  trackEarlyAccessSignup,
  trackNewsletterOptIn,
} from '../../utils/enhancedAnalytics';
import { supabase } from '../../utils/supabaseClient';
import { processEarlyAccessRequest } from '../../domain/earlyAccessHandler';
import {
  EARLY_ACCESS_PATH,
  GENERIC_SIGNUP_ERROR,
  INVALID_EMAIL_ERROR,
  analyticsPayloadContainsEmail,
  buildSignupAnalyticsPayload,
  collectClientAttribution,
  isValidEmail,
  publicErrorMessage,
} from '../../domain/earlyAccessSignup';
import './EarlyAccessPage.css';

const DOCUMENT_TITLE = 'Angler Built LiveScope Builder Early Access';

const BETA_JOBS = [
  {
    title: 'Choose the right LiveScope',
    body: 'Compare the tradeoffs that actually matter for how you fish.',
  },
  {
    title: 'Check your Garmin gear',
    body: 'See what works together and what you can keep using.',
  },
  {
    title: 'Build the complete ice setup',
    body: 'Identify what you already own and what is still needed.',
  },
];

const LIVE_TOOLS = [
  { to: '/comparisons/lvs42hd-vs-lvs44', label: 'Compare LiveScope 2' },
  { to: '/compatibility', label: 'Check Compatibility' },
  { to: '/price-comparison', label: 'See Current Prices' },
];

function trackSafeEvent(track, payload, email) {
  if (analyticsPayloadContainsEmail(payload, email)) return;
  track(payload);
}

export default function EarlyAccessPage() {
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [marketingAcknowledged, setMarketingAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    let robots = document.querySelector('meta[name="robots"]');
    let createdRobots = false;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
      createdRobots = true;
    }
    const previousRobots = robots.getAttribute('content');
    document.title = DOCUMENT_TITLE;
    robots.setAttribute('content', 'noindex, follow');
    return () => {
      document.title = previousTitle;
      if (createdRobots) robots.remove();
      else if (previousRobots != null) robots.setAttribute('content', previousRobots);
      else robots.removeAttribute('content');
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');

    if (!isValidEmail(email)) {
      setStatus('error');
      setErrorMessage(INVALID_EMAIL_ERROR);
      return;
    }

    const attribution = {
      ...getInboundUtmContext(location.search),
      ...collectClientAttribution({
        search: location.search,
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        pathname: location.pathname || EARLY_ACCESS_PATH,
      }),
    };

    setSubmitting(true);
    try {
      const result = await processEarlyAccessRequest(
        {
          email,
          marketingConsent,
          honeypot,
          attribution,
        },
        { supabase }
      );

      if (!result.ok) {
        setStatus('error');
        setErrorMessage(publicErrorMessage(result.status));
        return;
      }

      const analyticsPayload = buildSignupAnalyticsPayload({
        marketingConsent,
        attribution,
      });
      trackSafeEvent(trackEarlyAccessSignup, analyticsPayload, email);
      if (marketingConsent) {
        trackSafeEvent(trackNewsletterOptIn, {
          page: analyticsPayload.page,
        }, email);
      }

      setMarketingAcknowledged(Boolean(result.marketingAcknowledged || marketingConsent));
      setStatus('success');
    } catch {
      setStatus('error');
      setErrorMessage(GENERIC_SIGNUP_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ab-ea" data-testid="early-access-page">
      <header className="ab-ea-hero">
        <p className="ab-ea-kicker">Angler Built Early Access</p>
        <h1 className="ab-ea-title">LiveScope Builder Early Access</h1>
        <p className="ab-ea-lede">
          I'm building the next version of Angler Built to help you choose the
          right LiveScope, check what works with the Garmin gear you already own,
          and build a complete portable ice setup.
        </p>
        <p className="ab-ea-lede ab-ea-lede-last">
          Join early access and I'll let you know when the beta opens.
        </p>
      </header>

      <section className="ab-ea-panel" aria-labelledby="early-access-form-heading">
        {status === 'success' ? (
          <div className="ab-ea-success" data-testid="early-access-success">
            <h2 id="early-access-form-heading" className="ab-ea-success-title">
              You're on the list.
            </h2>
            <p className="ab-ea-success-copy">
              I'll email you when the LiveScope Builder beta is ready.
            </p>
            {marketingAcknowledged ? (
              <p className="ab-ea-success-note" data-testid="early-access-marketing-ack">
                You'll also get occasional FGTN and Angler Built fishing gear updates.
              </p>
            ) : null}
          </div>
        ) : (
          <form className="ab-ea-form" onSubmit={handleSubmit} noValidate>
            <h2 id="early-access-form-heading" className="ab-ea-form-title">
              Get notified when the beta opens
            </h2>
            <p className="ab-ea-purpose">
              Submitting your email asks me to send LiveScope Builder early access
              and beta updates only. Broader fishing gear updates are optional.
            </p>

            <div className="ab-ea-field">
              <label htmlFor="early-access-email">Email</label>
              <input
                id="early-access-email"
                name="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                data-testid="early-access-email"
              />
            </div>

            <div className="ab-ea-honeypot" aria-hidden="true">
              <label htmlFor="early-access-company">Company website</label>
              <input
                id="early-access-company"
                name="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                data-testid="early-access-honeypot"
              />
            </div>

            <label className="ab-ea-check">
              <input
                type="checkbox"
                checked={marketingConsent}
                onChange={(event) => setMarketingConsent(event.target.checked)}
                data-testid="early-access-marketing"
              />
              <span>Also send me FGTN and occasional Angler Built fishing gear updates.</span>
            </label>

            {status === 'error' ? (
              <p className="ab-ea-error" role="alert" data-testid="early-access-error">
                {errorMessage || GENERIC_SIGNUP_ERROR}
              </p>
            ) : null}

            <button
              type="submit"
              className="ab-ea-submit"
              disabled={submitting}
              data-testid="early-access-submit"
            >
              {submitting ? 'Joining early access...' : 'Join early access'}
            </button>
          </form>
        )}
      </section>

      <section className="ab-ea-jobs" aria-labelledby="early-access-jobs-heading">
        <h2 id="early-access-jobs-heading" className="ab-ea-jobs-title">
          What this beta is being built to support
        </h2>
        <ul className="ab-ea-job-list">
          {BETA_JOBS.map((job) => (
            <li key={job.title}>
              <p className="ab-ea-job-name">{job.title}</p>
              <p className="ab-ea-job-body">{job.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="ab-ea-now" aria-labelledby="early-access-now-heading">
        <h2 id="early-access-now-heading" className="ab-ea-now-title">
          Shopping for LiveScope now?
        </h2>
        <ul className="ab-ea-now-links">
          {LIVE_TOOLS.map((tool) => (
            <li key={tool.to}>
              <Link to={tool.to}>{tool.label}</Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
